import React from 'react'
import { Controller } from 'react-hook-form'
import Dropzone from 'react-dropzone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import ViewSensitivePiiButtons from '../../../../components/ViewSensitivePiiButtons'

/**
 * One document slot: shows either the existing-doc viewer or a drag-and-drop
 * zone, plus an optional "Is Active" checkbox.
 *
 * Behaviour-preserving extraction from DmcModal.jsx. Parent passes everything
 * as props; no internal state.
 */
export default function DmcDocumentSlot({
  slotKey, // 'dmc' | 'poa' | 'tpc' | 'ach' | 'notice'
  label,
  docId,
  docTypeId,
  file,
  onDrop,
  customerId,
  control, // null for the 'dmc' slot (no Is Active)
  isActiveFieldName, // e.g. 'poa_is_active'; null for the 'dmc' slot
  isActiveDisabled,
  tooltipText,
  tooltipPosition = 'top', // position of the Is Active info tooltip
  viewPiiTooltipPosition = 'bottom', // matches ViewSensitivePiiButtons own default
  docColumnPaddingLeft = true, // false = add pl-0 to remove default left padding; true = keep it
}) {
  // When there is no Is Active checkbox (DMC slot), the doc column always
  // takes three-quarters width. When there is a checkbox, the width depends
  // on whether a doc already exists (existing doc → half, upload → three-quarters).
  let docWidthClass = 'is-three-quarters-widescreen'
  if (isActiveFieldName && docId) {
    docWidthClass = 'is-half-widescreen'
  }
  const checkboxWidthClass = docId ? 'is-half-widescreen' : 'is-one-quarter-widescreen'
  const docColumnClass = `${docWidthClass} column py-2${docColumnPaddingLeft ? '' : ' pl-0'}`

  return (
    <div className="fields-container" data-testid={`dmc-doc-${slotKey}`}>
      <div className={docColumnClass}>
        <p className="has-text-grey is-small is-size-8">
          <span className="mr-1">{label}</span>
          {tooltipText && !isActiveFieldName && (
            <span
              style={{ pointerEvents: 'auto' }}
              className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-${tooltipPosition} has-tooltip-primary has-text-weight-normal`}
              data-tooltip={tooltipText}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="pl-1 has-text-link" />
            </span>
          )}
        </p>
        {docId && (
          <ViewSensitivePiiButtons
            customerId={customerId}
            documentId={docId}
            documentTypeId={docTypeId}
            type={'general_document'}
            style={{ left: '-10px' }}
            tooltipPosition={viewPiiTooltipPosition}
          />
        )}
        {!docId && (
          <Dropzone onDrop={onDrop}>
            {({ getRootProps, getInputProps }) => (
              <section className="container">
                <div
                  {...getRootProps({ className: 'dropzone' })}
                  data-testid={`dmc-doc-${slotKey}-dropzone`}
                  className="is-flex is-justify-content-center is-size-5 has-text-weight-bold fa-border is-clickable"
                  style={{ borderColor: 'gray', borderStyle: 'dashed', backgroundColor: 'lightgray' }}
                >
                  <input {...getInputProps()} />
                  <p>Drop File</p>
                </div>
                <aside>
                  <ul>
                    <li>{file && file[0]?.name}</li>
                  </ul>
                </aside>
              </section>
            )}
          </Dropzone>
        )}
      </div>

      {isActiveFieldName && (
        <div className={`${checkboxWidthClass} column py-2`}>
          <p className="has-text-grey is-small is-size-8">
            <span className="mr-1">
              Is Active
              <span
                style={{ pointerEvents: 'auto' }}
                className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-${tooltipPosition} has-tooltip-primary has-text-weight-normal`}
                data-tooltip={tooltipText}
              >
                <FontAwesomeIcon icon={faCircleInfo} className="pl-1 has-text-link" />
              </span>
            </span>
          </p>
          <label>
            <Controller
              name={isActiveFieldName}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <input
                  {...field}
                  className="checkbox is-small"
                  type="checkbox"
                  data-testid={`dmc-doc-${slotKey}-is-active`}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  disabled={isActiveDisabled}
                />
              )}
            />
          </label>
        </div>
      )}
    </div>
  )
}
